/**
 * Component Name: InvoiceTable
 * Description: A table component for displaying a list of invoices with sortable columns and clickable invoice numbers.
 * Props:
 *   - filteredInvoices (array): List of filtered invoice objects to display.
 *   - projects (array): List of project objects for mapping project IDs to names.
 *   - handleViewDetails (function): Callback to handle invoice detail view clicks.
 * Feature: Displays a sortable table of invoices with details like invoice number, project, supplier, and amounts, allowing users to click invoice numbers to view details in the invoice management interface.
 */
import React from 'react';
import CustomTable from '../table/table';
import { toUSD } from '../../util/currency-formatter';
import idx from 'idx';
import { GSTORVAT } from '../../util/enums';
import { INVOICE_CSS_CLASSES, INVOICE_STATUS, INVOICE_TABLE_COLUMNS } from '../../util/enums/invoice-management';
const InvoiceTable = ({ filteredInvoices, projects, handleViewDetails }) => {
    const invoices = filteredInvoices.filter(invoices => invoices.invoiceId);

    const data = invoices.map(invoice => {
        const projectName = projects.find(p => p.id === invoice.projectId)?.name || INVOICE_STATUS.NA;
        return {
            invoiceId: invoice.invoiceId,
            invoiceNumber: invoice.invoiceNumber,
            projectId: invoice.projectId,
            projectName: projectName,
            sectionHierarchy: invoice.sectionHierarchy,
            poNumber: idx(invoice, _ => _.PurchaseOrders.id),
            procurementCode: idx(invoice, _ => _.PurchaseOrders.f_id),
            supplier: invoice.supplier,
            issueDate: invoice.issueDate,
            dueDate: invoice.dueDate,
            totalAmount: invoice.totalAmount,
            subTotal: invoice.subTotal,
            userName: invoice.userName,
            status: invoice.status
        }
    });

    const columns = [
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_NUMBER,
            field: 'invoiceNumber',
            columnSearch: true,
            render: (row) => (
                <span className={INVOICE_CSS_CLASSES.INVOICE_NUMBER} onClick={() => handleViewDetails(row.invoiceId)}>
                    {row.invoiceNumber}
                </span>
            ),
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PROJECT,
            field: 'projectName',
            columnSearch: true,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.SECTION_SUBSECTION,
            field: 'sectionHierarchy',
            columnSearch: true,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PO_NUMBER,
            field: 'poNumber',
            columnSearch: true,
            sortable: true,
            /* procurementCode is the PO number with project prefix and Suffix if null then default to po Number*/
            render: (row) => <span className={INVOICE_CSS_CLASSES.PO_NUMBER}>{row.procurementCode || row.poNumber}</span>,
        },
        {
            title: INVOICE_TABLE_COLUMNS.SUPPLIER,
            field: 'supplier',
            columnSearch: true,
            render: (row) => <span className={INVOICE_CSS_CLASSES.SUPPLIER_NAME}>{row.supplier || 'N/A'}</span>,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.ISSUE_DATE,
            field: 'issueDate',
            type: 'date',
            columnSearch: true,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.DUE_DATE,
            field: 'dueDate',
            type: 'date',
            columnSearch: true,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.AMOUNT,
            field: 'totalAmount',
            class: 'text-right justify-content-end',
            columnSearch: true,
            render: (row) => <span className={INVOICE_CSS_CLASSES.AMOUNT}>{toUSD(row.totalAmount)}</span>,
            sortable: true,
        },
        {
            title: `${INVOICE_TABLE_COLUMNS.SUB_TOTAL} \n(Ex. ${GSTORVAT})`,
            field: 'subTotal',
            class: 'text-right justify-content-end',
            columnSearch: true,
            render: (row) => <span className={INVOICE_CSS_CLASSES.SUB_TOTAL_AMOUNT}>{toUSD(row.subTotal)}</span>,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.RECEIVED_BY,
            field: 'userName',
            columnSearch: true,
            sortable: true,
        }
    ];

    const tableItemClickHandler = (row) => {
        handleViewDetails(row.invoiceId);
    };


    return (
        <div className="table-container">
            <CustomTable columns={columns} data={data} pagination={true} rowClickHandler={tableItemClickHandler} />
        </div>
    );
};

export default InvoiceTable;